/**
 * @file src/pages/invoice-creation-wizard/components/WizardHeader.jsx
 *
 * What changed from the original:
 *  - Clock icon with animate-spin replaced with Loader2 when isSaving
 *    A spinning clock is confusing — Loader2 is the standard spinner icon
 *  - STEPS array moved outside component — not recreated on every render
 *  - formatRelativeTime moved above component (was already outside, kept)
 */

import React from "react";
import { motion } from "framer-motion";
import { Loader2, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";

// ---------------------------------------------------------------------------
// Static data
// ---------------------------------------------------------------------------

const STEPS = [
  { id: 1, titleKey: "invoiceWizard.steps.customer.title", subtitleKey: "invoiceWizard.steps.customer.subtitle" },
  { id: 2, titleKey: "invoiceWizard.steps.invoice.title", subtitleKey: "invoiceWizard.steps.invoice.subtitle" },
  { id: 3, titleKey: "invoiceWizard.steps.items.title", subtitleKey: "invoiceWizard.steps.items.subtitle" },
  { id: 4, titleKey: "invoiceWizard.steps.review.title", subtitleKey: "invoiceWizard.steps.review.subtitle" },
];

const formatRelativeTime = (date) => {
  const diff = Date.now() - date;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

// ---------------------------------------------------------------------------
// WizardHeader
// ---------------------------------------------------------------------------

const WizardHeader = ({
  currentStep,
  onStepChange,
  onBack,
  onSave,
  onPreview,
  isSaving,
  lastSaved,
  autoSaveEnabled,
  onAutoSaveToggle,
}) => {
  const { t } = useTranslation("pages");

  return (
  <motion.div
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    className="sticky top-0 z-40 bg-white dark:bg-gray-800 rounded-xl shadow-hero-cta border border-gray-100 dark:border-gray-700 mb-8"
  >
    <div className="p-6">
      {/* ── Header top ────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            iconName="ArrowLeft"
            onClick={onBack}
            className="p-2"
          />
          <div>
            <h1 className="text-2xl font-bold text-foreground dark:text-white">
              {t("invoiceWizard.title")}
            </h1>
            <p className="text-muted-foreground dark:text-gray-300">
              {t("invoiceWizard.description")}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Auto-save status */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground dark:text-gray-300">
            <div className="flex items-center gap-1">
              {/* Loader2 when saving, Clock when idle — Clock doesn't "spin" */}
              {isSaving ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Clock size={14} />
              )}
              <span>
                {isSaving
                  ? t("invoiceWizard.saving")
                  : lastSaved
                    ? t("invoiceWizard.saved", {
                        time: formatRelativeTime(lastSaved),
                      })
                    : t("invoiceWizard.notSaved")}
              </span>
            </div>
            <button
              onClick={onAutoSaveToggle}
              aria-pressed={autoSaveEnabled}
              aria-label={t("invoiceWizard.autoSaveState", {
                state: autoSaveEnabled
                  ? t("invoiceWizard.autoSaveOn")
                  : t("invoiceWizard.autoSaveOff"),
              })}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                autoSaveEnabled
                  ? "bg-success-100 text-success-700 dark:bg-success-900/20 dark:text-success-400"
                  : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
              }`}
            >
              {t("invoiceWizard.autoSave")}{" "}
              {autoSaveEnabled
                ? t("invoiceWizard.autoSaveOn")
                : t("invoiceWizard.autoSaveOff")}
            </button>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Save"
              onClick={onSave}
              disabled={isSaving}
            >
              {t("invoiceWizard.save")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Eye"
              onClick={onPreview}
            >
              {t("invoiceWizard.preview")}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Progress Steps ────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => (
          <React.Fragment key={step.id}>
            <div
              className={`flex items-center gap-3 cursor-pointer transition-all duration-200 ${
                currentStep >= step.id
                  ? "text-primary"
                  : "text-muted-foreground dark:text-gray-400"
              }`}
              onClick={() => onStepChange(step.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && onStepChange(step.id)}
              aria-label={`Go to step ${step.id}: ${t(step.titleKey)}`}
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all duration-200 ${
                  currentStep >= step.id
                    ? "bg-primary text-white"
                    : "bg-gray-200 dark:bg-gray-700 text-muted-foreground dark:text-gray-400"
                }`}
              >
                {currentStep > step.id ? (
                  <Icon name="CheckCircle2" size={20} />
                ) : (
                  step.id
                )}
              </div>
              <div className="hidden sm:block">
                <div className="font-medium">{t(step.titleKey)}</div>
                <div className="text-xs text-muted-foreground dark:text-gray-400">
                  {t(step.subtitleKey)}
                </div>
              </div>
            </div>

            {index < STEPS.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-4 transition-all duration-200 ${
                  currentStep > step.id
                    ? "bg-primary"
                    : "bg-gray-200 dark:bg-gray-700"
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  </motion.div>
  );
};

export default WizardHeader;
